import React, { useCallback, useEffect } from 'react';
import { useMetricsFilter } from 'contexts/metricsFilter/useMetricsFilter';
import { DateRangeStateType, DateRangeType } from 'components/DateRange/DateRange.types';
import DateService from 'services/DateService/dateService';
import moment from 'moment';
import { FilterContainer, FilterButton, StyedButton, SelectStyled, DateRangeStyled } from './MetricsFilter.styles';
import { IMetricsFilterProps } from './MetricsFilter.types';

const MAX_ALLOWED_DAYS = 365;

export const MetricsFilter = ({ style }: IMetricsFilterProps) => {
  const [companiesSelectedFilter, setCompaniesSelectedFilter] = React.useState<string[]>([]);
  const [startDateSelectedFilter, setStartDateSelectedFilter] = React.useState<string>(DateService.getDateFromToday());
  const [endDateSelectedFilter, setEndDateSelectedFilter] = React.useState<string>(DateService.getDateFromTomorrow());
  const [momentStartDateSelectedFilter, setMomentStartDateSelectedFilter] = React.useState<DateRangeStateType>(
    DateService.getDateFromTodayMoment()
  );
  const [momentEndDateSelectedFilter, setMomentEndDateSelectedFilter] = React.useState<DateRangeStateType>(
    DateService.getDateFromTomorrowMoment()
  );
  const { companyOptions, companiesSelected, startDateSelected, endDateSelected, setCompaniesSelected, updateFilters } =
    useMetricsFilter();

  const onDatesChange = (range: { startDate: DateRangeType; endDate: DateRangeType }): void => {
    const { startDate, endDate } = range;

    setStartDateFilters(startDate);
    setEndDateFilters(limitEndDate(startDate, endDate));
  };

  const limitEndDate = (startDate: DateRangeType, endDate: DateRangeType | null): DateRangeType | null => {
    if (!endDate) return null;

    const maxEndDate = moment(startDate).add(MAX_ALLOWED_DAYS, 'days');
    return endDate.isAfter(maxEndDate) ? maxEndDate : endDate;
  };

  const setEndDateFilters = (endDate: DateRangeType | null): void => {
    setMomentEndDateSelectedFilter(endDate);
    setEndDateSelectedFilter(endDate ? DateService.fromMomentToIsoLessThan(endDate) : '');
  };

  const setStartDateFilters = (startDate: DateRangeType | null): void => {
    setMomentStartDateSelectedFilter(startDate);
    setStartDateSelectedFilter(startDate ? DateService.fromMomentToIsoGreaterThan(startDate) : '');
  };

  const handleUpdateFilters = () => {
    updateFilters(companiesSelectedFilter, [], [], null, null, startDateSelectedFilter, endDateSelectedFilter);
  };

  const isFilterDisabled = () => {
    return companiesSelectedFilter.length === 0 || startDateSelectedFilter === '' || endDateSelectedFilter === '';
  };

  const updateSelectedFilterFromContext = useCallback(() => {
    if (companiesSelectedFilter.join() !== companiesSelected.join()) {
      setCompaniesSelectedFilter(companiesSelected);
    }

    if (startDateSelectedFilter !== startDateSelected) {
      setMomentStartDateSelectedFilter(moment(startDateSelected).add(1, 'days'));
    }

    if (endDateSelectedFilter !== endDateSelected) {
      setMomentEndDateSelectedFilter(moment(endDateSelected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setCompaniesSelectedFilter([]);
    setStartDateSelectedFilter('');
    setEndDateSelectedFilter('');
  };

  const handleSelectAllComapnies = useCallback(() => {
    if (companyOptions.length > 0) {
      const options = companyOptions.map((company) => company.value);
      setCompaniesSelectedFilter(options);
      setCompaniesSelected(options);
    }
  }, [companyOptions, setCompaniesSelected]);

  useEffect(() => {
    handleSelectAllComapnies();
  }, [handleSelectAllComapnies]);

  useEffect(() => {
    updateSelectedFilterFromContext();

    return () => {
      resetFilters();
    };
  }, [updateSelectedFilterFromContext]);

  return (
    <FilterContainer style={style} data-testid="metrics-filter">
      <DateRangeStyled
        className="animate fadeIn"
        testID="date-range-select"
        startDate={momentStartDateSelectedFilter}
        endDate={momentEndDateSelectedFilter}
        label="Período"
        onDatesChange={onDatesChange}
      />

      <SelectStyled
        label="Operação"
        labelId="companies"
        options={companyOptions}
        selectedOptions={companiesSelectedFilter}
        onChange={({ target: { value } }): void => setCompaniesSelectedFilter(value as string[])}
        multiple
        placeholder="Selecione as empresas"
      />
      <FilterButton>
        <StyedButton text="Filtrar" onClick={handleUpdateFilters} disabled={isFilterDisabled()} capitalize />
      </FilterButton>
    </FilterContainer>
  );
};
